import {
  useActivePractice,
  useDispatch,
  usePracticeProperties,
} from '@witmetrics/data-store';
import type { DecoratedLead } from '@witmetrics/utils';
import { isBlank } from '@witmetrics/utils/strings';
import { isEmpty } from '@witmetrics/utils/misc';
import { API } from '@/api';
import { showErrorToaster, showSuccessToaster } from '@/utils/toasters';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { useString } from '@/hooks/useString';
import { useInvalidFields } from '@/hooks/useInvalidFields';
import TextField from '@/components/inputs/TextField';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import { buildNewLeadProperties, classes } from './utils';

export type LeadDialogProps = {
  initialFirstname?: string;
  initialLastname?: string;
  onSelect?: (lead: DecoratedLead) => void;
  onClose: () => void;
};

export default function NewLeadDialog({
  initialFirstname = '',
  initialLastname = '',
  onSelect,
  onClose,
}: LeadDialogProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const practiceProperties = usePracticeProperties();
  const activePractice = useActivePractice();
  const [firstname, setFirstname] = useString(initialFirstname);
  const [lastname, setLastname] = useString(initialLastname);
  const [externalRef, setExternalRef] = useString();
  const [email, setEmail] = useString();
  const [phone, setPhone] = useString();
  const { setInvalidFields, isValidField } = useInvalidFields();
  const [isSaving, toggleSaving] = useToggle();

  const handleSave = async () => {
    try {
      toggleSaving(true);
      if (isBlank(firstname)) {
        toggleSaving(false);
        return setInvalidFields(['firstname']);
      }
      if (!isEmpty(email)) {
        const emailData = await API.Users.validateEmail(email);
        if (!emailData.isValid) {
          showErrorToaster(emailData.message);
          toggleSaving(false);
          return setInvalidFields(['email']);
        }
      }
      return handleCreateLead();
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error saving lead', () => handleSave());
    }
  };

  const getLeadProperties = () => {
    return buildNewLeadProperties({ practiceProperties, phone });
  };

  const handleCreateLead = async () => {
    try {
      if (!activePractice) return;
      const leadProperties = getLeadProperties();
      const newLead = await API.Leads.createNewLead({
        firstname,
        lastname,
        email,
        externalRef,
        properties: leadProperties,
        practiceId: activePractice.id,
      });
      dispatch.addLeads([newLead]);
      showSuccessToaster('Lead created');
      toggleSaving(false);
      if (onSelect) onSelect(newLead);
      onClose();
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error creating lead', () => handleCreateLead());
    }
  };

  return (
    <Dialog
      className={classes.dialog}
      onClose={() => null /* Prevent accidentally closing it */}>
      <DialogTitle title="New Patient" onClose={onClose} />
      <DialogBody className={classes.body}>
        <div className={classes.section}>
          <TextField
            autoFocus
            error={!isValidField('firstname')}
            className={classes.input}
            label="First name"
            value={firstname}
            onChange={setFirstname}
          />
          <div className={classes.optional}>Optional fields</div>
          <TextField
            className={classes.input}
            label="Last name"
            value={lastname}
            onChange={setLastname}
          />
          <TextField
            className={classes.input}
            label="External Ref."
            value={externalRef}
            onChange={setExternalRef}
          />
          <TextField
            error={!isValidField('email')}
            className={classes.input}
            label="Email"
            value={email}
            onChange={setEmail}
          />
          <TextField
            className={classes.input}
            label="Phone"
            value={phone}
            onChange={setPhone}
          />
        </div>
      </DialogBody>
      <DefaultDialogActions
        disabled={isSaving || isBlank(firstname)}
        pending={isSaving}
        confirmLabel="Save"
        pendingLabel="Saving"
        onCancel={onClose}
        onConfirm={handleSave}
      />
    </Dialog>
  );
}
